import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type SheetSelectionContextValue = {
  selectedSheetId: number | null;
  setSelectedSheetId: (id: number | null) => void;
  selectedCategoryIds: number[];
  setSelectedCategoryIds: (ids: number[]) => void;
  selectedTagIds: number[];
  setSelectedTagIds: (ids: number[]) => void;
};

const SheetSelectionContext = createContext<SheetSelectionContextValue | null>(null);

export const SheetSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedSheetId, setSelectedSheetId] = useState<number | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  const handleSetSelectedSheetId = useCallback((id: number | null) => {
    setSelectedSheetId((prev) => (prev === id ? prev : id));
    setSelectedTagIds((prev) => (prev.length === 0 ? prev : []));
  }, []);

  const handleSetSelectedCategoryIds = useCallback((ids: number[]) => {
    setSelectedCategoryIds((prev) => {
      if (prev.length === ids.length && prev.every((value, index) => value === ids[index])) {
        return prev;
      }
      return ids;
    });
    if (ids.length > 0) {
      setSelectedTagIds((prev) => (prev.length === 0 ? prev : []));
    }
  }, []);

  const value = useMemo(
    () => ({
      selectedSheetId,
      setSelectedSheetId: handleSetSelectedSheetId,
      selectedCategoryIds,
      setSelectedCategoryIds: handleSetSelectedCategoryIds,
      selectedTagIds,
      setSelectedTagIds,
    }),
    [
      selectedSheetId,
      handleSetSelectedSheetId,
      selectedCategoryIds,
      handleSetSelectedCategoryIds,
      selectedTagIds,
      setSelectedTagIds,
    ]
  );

  return (
    <SheetSelectionContext.Provider value={value}>
      {children}
    </SheetSelectionContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSheetSelection = () => {
  const ctx = useContext(SheetSelectionContext);
  if (!ctx) throw new Error('useSheetSelection must be used within SheetSelectionProvider');
  return ctx;
};

export default SheetSelectionProvider;
